import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from '@/components/ui/button';

const LOGO_URL = "https://customer-assets.emergentagent.com/job_7ca2cd33-9325-433c-b3f0-102d7d17319b/artifacts/tie58z5q_vfcs9rps_tridenterprise_logo_transparent.png";
const WHATSAPP_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/hx48xypq_whatsapp.png";
const MAIL_ICON = "https://customer-assets.emergentagent.com/job_d9a33add-2dfd-4326-bbed-949961573d64/artifacts/wmk5drle_mail.png";

const navLinks = [
  { name: 'Home', path: '/' },
  { name: 'Services', path: '/services' },
  { name: 'Case Studies', path: '/case-studies' },
  { name: 'Testimonials', path: '/testimonials' },
  { name: 'About', path: '/about' },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const isActive = (path) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <header className="fixed top-0 left-0 right-0 z-50 glass-header" data-testid="main-header">
      <div className="container-custom">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center" data-testid="header-logo">
            <img 
              src={LOGO_URL} 
              alt="Trid Enterprise Solutions" 
              className="w-[180px] md:w-[220px] h-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center gap-10" data-testid="desktop-nav">
            {navLinks.map((link) => (
              <Link
                key={link.name}
                to={link.path}
                className={`font-body text-sm font-medium transition-colors whitespace-nowrap ${
                  isActive(link.path) ? 'text-oracle-red' : 'text-slate-200 hover:text-white'
                }`}
                data-testid={`nav-link-${link.name.toLowerCase().replace(' ', '-')}`}
              >
                {link.name}
              </Link>
            ))}
          </nav>

          {/* CTA Buttons with Quick Contact Icons */}
          <div className="hidden lg:flex items-center space-x-3">
            {/* Quick Contact Icons */}
            <div className="flex items-center space-x-2 mr-2">
              <a 
                href="https://wa.me/919160919609" 
                target="_blank" 
                rel="noopener noreferrer"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800/50 hover:bg-slate-700 transition-all hover:scale-110"
                title="WhatsApp Us"
                data-testid="header-whatsapp-cta"
              >
                <img src={WHATSAPP_ICON} alt="WhatsApp" className="w-5 h-5" />
              </a>
              <a 
                href="mailto:info@tridenterprise.com"
                className="w-9 h-9 flex items-center justify-center rounded-full bg-slate-800/50 hover:bg-slate-700 transition-all hover:scale-110"
                title="Email Us"
                data-testid="header-email-cta"
              >
                <img src={MAIL_ICON} alt="Email" className="w-5 h-5" />
              </a>
            </div>
            <Link to="/contact">
              <Button 
                variant="outline" 
                className="border-slate-600 text-slate-200 hover:bg-slate-800 hover:text-white font-body"
                data-testid="header-contact-btn"
              >
                Contact Sales
              </Button>
            </Link>
            <Link to="/contact">
              <Button 
                className="bg-oracle-red hover:bg-oracle-red-dark text-white font-body"
                data-testid="header-consultation-btn"
              >
                Schedule Consultation
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-btn"
          >
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden mobile-menu-enter pb-6 border-t border-slate-700 mt-4 pt-4" data-testid="mobile-menu">
            <nav className="flex flex-col space-y-2">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`block font-body text-base font-medium py-3 px-2 rounded-sm transition-colors ${
                    isActive(link.path) ? 'text-oracle-red bg-slate-800/50' : 'text-slate-200 hover:bg-slate-800/30'
                  }`}
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {link.name}
                </Link>
              ))}
              {/* Mobile Quick Contact */}
              <div className="flex items-center justify-center space-x-4 py-4 border-t border-slate-700 mt-2">
                <a 
                  href="https://wa.me/919160919609" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="flex items-center space-x-2 text-slate-200 hover:text-white transition-colors"
                >
                  <img src={WHATSAPP_ICON} alt="WhatsApp" className="w-5 h-5" />
                  <span className="font-body text-sm">WhatsApp</span>
                </a>
                <a 
                  href="mailto:info@tridenterprise.com"
                  className="flex items-center space-x-2 text-slate-200 hover:text-white transition-colors"
                >
                  <img src={MAIL_ICON} alt="Email" className="w-5 h-5" />
                  <span className="font-body text-sm">Email</span>
                </a>
              </div>
              <div className="space-y-3">
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button 
                    variant="outline" 
                    className="w-full border-slate-600 text-slate-200 py-5"
                  >
                    Contact Sales
                  </Button>
                </Link>
                <Link to="/contact" onClick={() => setMobileMenuOpen(false)} className="block">
                  <Button 
                    className="w-full bg-oracle-red hover:bg-oracle-red-dark text-white py-5"
                  >
                    Schedule Consultation
                  </Button>
                </Link>
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
